import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, BookMarked, Copy, Eye, MoreHorizontal, Pencil, Plus, RotateCcw, Search, Tag, Trash2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { isTemplateEmpty } from '@/lib/templateContent';
import { NO_CATEGORY, groupLibraryPages, usePageLibraryStore } from '@/store/pageLibraryStore';
import { BlockType } from '@/types';
import type { Template } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import TemplatePreviewDialog from '@/components/TemplatePreviewDialog';
import LibraryPageDetailsDialog, { type LibraryPageDetails } from './LibraryPageDetailsDialog';

export type LibrarySortOption = 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc';

interface PageLibraryTabProps {
  portalId?: string;
  searchQuery: string;
  onClearSearch: () => void;
  sortBy: LibrarySortOption;
  includeArchived: boolean;
  /** De knop "Nieuwe pagina" staat in de kop van het overzicht, naast de tabs. */
  newPageOpen: boolean;
  onNewPageOpenChange: (open: boolean) => void;
}

function sortPages(pages: Template[], sortBy: LibrarySortOption) {
  return [...pages].sort((a, b) => {
    switch (sortBy) {
      case 'created_desc': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'created_asc': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'name_asc': return a.name.localeCompare(b.name, 'nl');
      case 'name_desc': return b.name.localeCompare(a.name, 'nl');
      default: return 0;
    }
  });
}

/**
 * De paginabibliotheek: losse pagina's (Over UBO, werkwijze, voorwaarden…) die je in
 * elke offerte invoegt via Pagina toevoegen. Een bibliotheekpagina is een template van
 * het soort `page`, dus bewerken gaat via dezelfde werkkopie in de editor.
 */
export default function PageLibraryTab({
  portalId,
  searchQuery,
  onClearSearch,
  sortBy,
  includeArchived,
  newPageOpen,
  onNewPageOpenChange,
}: PageLibraryTabProps) {
  const navigate = useNavigate();
  const pages = usePageLibraryStore((state) => state.pages);
  const loaded = usePageLibraryStore((state) => state.loaded);
  const loadError = usePageLibraryStore((state) => state.error);
  const fetchPages = usePageLibraryStore((state) => state.fetchPages);

  const [preview, setPreview] = useState<Template | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<Template | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bij elk bezoek opnieuw: een pagina kan net in de editor zijn opgeslagen.
  useEffect(() => {
    void fetchPages();
  }, [fetchPages]);

  const visiblePages = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return sortPages(
      pages.filter(
        (page) =>
          (includeArchived || !page.archivedAt) &&
          (!query ||
            [page.name, page.category, page.description].some((text) => text?.toLowerCase().includes(query)))
      ),
      sortBy
    );
  }, [pages, searchQuery, sortBy, includeArchived]);

  /** Per categorie: de standaardpagina's vooraan, pagina's zonder categorie achteraan. Zonder categorieën één lijst. */
  const groups = useMemo(() => groupLibraryPages(visiblePages), [visiblePages]);
  const showGroupHeaders = groups.length > 1 || (groups.length === 1 && groups[0][0] !== NO_CATEGORY);

  const handleCreate = async ({ name, category, description }: LibraryPageDetails) => {
    try {
      const page = await api.createTemplate({
        name,
        kind: 'page',
        category: category || undefined,
        description: description || undefined,
        blocks: [
          {
            type: BlockType.CONTENT_PAGE,
            order: 0,
            content: { title: '', content: '<p></p>', style: 'default' },
          },
        ],
      });
      onNewPageOpenChange(false);
      navigate(`/templates/${page.id}/edit`);
    } catch (error) {
      toast.error('Pagina aanmaken mislukt', { error });
    }
  };

  const handleUpdateDetails = async ({ name, category, description }: LibraryPageDetails) => {
    if (!detailsTarget) return;
    try {
      await api.updateTemplateDetails(detailsTarget.id, {
        name,
        category: category || null,
        description: description || null,
      });
      await fetchPages();
      setDetailsTarget(null);
      toast.success('Pagina bijgewerkt');
    } catch (error) {
      toast.error('Opslaan mislukt', { error });
    }
  };

  const handleDuplicate = async (page: Template) => {
    try {
      await api.duplicateTemplate(page.id);
      await fetchPages();
      toast.success('Pagina gedupliceerd');
    } catch (error) {
      toast.error('Dupliceren mislukt', { error });
    }
  };

  const handleArchive = async (page: Template) => {
    if (!portalId) return;
    try {
      if (page.archivedAt) {
        await api.unarchiveTemplate(page.id, portalId);
        toast.success('Pagina hersteld');
      } else {
        await api.archiveTemplate(page.id, portalId);
        toast.success('Pagina gearchiveerd; hij staat niet meer in Pagina toevoegen');
      }
      await fetchPages();
    } catch (error) {
      toast.error(page.archivedAt ? 'Herstellen mislukt' : 'Archiveren mislukt', { error });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.deleteTemplate(deleteTarget.id);
      await fetchPages();
      toast.success('Pagina verwijderd');
      setDeleteTarget(null);
    } catch (error) {
      toast.error('Verwijderen mislukt', { error });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      {loadError && pages.length === 0 ? (
        <div className="py-16 text-center">
          <p className="mb-4 text-sm text-muted-foreground">
            {loadError.message} <ErrorReportButton report={loadError.report} />
          </p>
          <Button variant="outline" onClick={() => void fetchPages()}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Opnieuw proberen
          </Button>
        </div>
      ) : !loaded ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-40 rounded-lg border bg-card animate-pulse" />
          ))}
        </div>
      ) : pages.length === 0 ? (
        <div className="py-16 text-center">
          <BookMarked className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
          <h2 className="text-xl font-semibold text-muted-foreground mb-2">Nog geen pagina’s in de bibliotheek</h2>
          <p className="mx-auto max-w-md text-sm text-muted-foreground mb-6">
            Bewaar een pagina uit een offerte met het bladwijzer-icoon in de paginalijst, of maak er hier
            een. Daarna voeg je hem in elke offerte in via Pagina toevoegen.
          </p>
          <Button variant="outline" onClick={() => onNewPageOpenChange(true)} disabled={!portalId}>
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe pagina
          </Button>
        </div>
      ) : visiblePages.length === 0 ? (
        <div className="py-16 text-center">
          <Search className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
          <h2 className="text-lg font-semibold text-muted-foreground mb-2">Geen pagina’s gevonden</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery.trim() ? 'Pas je zoekopdracht aan' : 'Alle pagina’s zijn gearchiveerd'}
          </p>
          {searchQuery.trim() && (
            <Button variant="outline" onClick={onClearSearch}>
              Wis zoekopdracht
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map(([category, categoryPages]) => (
            <section key={category}>
              {showGroupHeaders && (
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  {category}
                  <span className="text-xs font-normal">({categoryPages.length})</span>
                </h2>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {categoryPages.map((page) => (
                  <LibraryPageCard
                    key={page.id}
                    page={page}
                    onEdit={() => navigate(`/templates/${page.id}/edit`)}
                    onPreview={() => setPreview(page)}
                    onEditDetails={() => setDetailsTarget(page)}
                    onDuplicate={() => void handleDuplicate(page)}
                    onArchive={() => void handleArchive(page)}
                    onDelete={() => setDeleteTarget(page)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <TemplatePreviewDialog open={Boolean(preview)} template={preview} onClose={() => setPreview(null)} />

      <LibraryPageDetailsDialog
        open={newPageOpen}
        page={null}
        pages={pages}
        onOpenChange={onNewPageOpenChange}
        onSubmit={handleCreate}
      />
      <LibraryPageDetailsDialog
        open={Boolean(detailsTarget)}
        page={detailsTarget}
        pages={pages}
        onOpenChange={(open) => !open && setDetailsTarget(null)}
        onSubmit={handleUpdateDetails}
      />

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open && !deleteLoading) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Verwijderen bevestigen</DialogTitle>
            <DialogDescription>
              Weet je zeker dat je &ldquo;{deleteTarget?.name}&rdquo; uit de bibliotheek wilt verwijderen?
              Offertes waar hij al in staat, houden hun kopie.
              {deleteTarget?.draftQuoteId && ' Ook het concept met niet-opgeslagen wijzigingen gaat weg.'} Dit kan
              niet ongedaan worden gemaakt; archiveren kan wel.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
              Annuleren
            </Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={deleteLoading}>
              {deleteLoading ? 'Verwijderen...' : 'Verwijderen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LibraryPageCard({
  page,
  onEdit,
  onPreview,
  onEditDetails,
  onDuplicate,
  onArchive,
  onDelete,
}: {
  page: Template;
  onEdit: () => void;
  onPreview: () => void;
  onEditDetails: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const isEmpty = isTemplateEmpty(page);
  const pageCount = page.blocks.length;

  return (
    <Card className={cn('group flex flex-col transition-shadow hover:shadow-md', isEmpty && 'border-dashed')}>
      <CardContent className="flex-1 pt-6">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-base font-semibold leading-tight line-clamp-2">{page.name}</h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0 text-muted-foreground" aria-label="Meer acties">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-4 w-4" />
                Inhoud bewerken
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEditDetails}>
                <Tag className="h-4 w-4" />
                Naam en categorie
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onPreview} disabled={isEmpty}>
                <Eye className="h-4 w-4" />
                Preview
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDuplicate}>
                <Copy className="h-4 w-4" />
                Dupliceren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onArchive}>
                {page.archivedAt ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                {page.archivedAt ? 'Herstellen' : 'Archiveren'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                <Trash2 className="h-4 w-4" />
                Verwijderen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {page.archivedAt && <Badge variant="outline" className="text-xs">Gearchiveerd</Badge>}
          {isEmpty && <Badge variant="outline" className="text-xs text-muted-foreground">Leeg</Badge>}
        </div>
        {isEmpty ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Deze pagina heeft nog geen inhoud en staat daarom niet in Pagina toevoegen.
          </p>
        ) : (
          page.description && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{page.description}</p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          {[
            isEmpty ? null : `${pageCount} ${pageCount === 1 ? 'pagina' : "pagina's"}`,
            `Aangemaakt ${new Date(page.createdAt).toLocaleDateString('nl-NL')}${
              page.createdBy ? ` door ${page.createdBy}` : ''
            }`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {page.draftQuoteId && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-700">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
            Concept nog niet opgeslagen in de bibliotheek
          </p>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap gap-1">
        <Button variant="ghost" size="sm" className="px-2" onClick={onEdit}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Bewerken
        </Button>
        {!isEmpty && (
          <Button variant="ghost" size="sm" className="px-2" onClick={onPreview}>
            <Eye className="mr-1.5 h-3.5 w-3.5" />
            Preview
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
